import React from 'react';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesAlertTitle,
  AgnesBadge,
  AgnesBarLine,
  AgnesBarLineProps,
  AgnesCard,
  AgnesCardContent,
  AgnesCardHeader,
  AgnesCardTitle,
  AgnesDonutChart,
  AgnesDonutChartProps,
  AgnesGaugeChart,
  AgnesLineChart,
  AgnesLineChartSeries,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSkeletonCard,
  AgnesStackedBar,
  AgnesStackedBarProps,
  AgnesStatCard,
  AgnesStatCardProps,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
} from '@agnes/ui';
import { ExternalLink, FileText, Info, TriangleAlert } from 'lucide-react';
import { MatrizGrauExposicaoRisco } from '@/components/analise/MatrizGrauExposicaoRisco';
import {
  GRAU_EXPOSICAO_RISCO_CORES,
  GRAU_EXPOSICAO_RISCO_NOMES,
} from '@/constants/grau-exposicao-risco/GrauExposicaoRisco';
import { brl } from '@/utils/locale';
import ServiceRubricas from '@/services/ServiceRubricas';
import { toOptions } from '@/utils/options';
import { toLocaleMonthName } from '@/utils/month';
import { SELECT_ALL_VALUES_OPTION } from '@/constants/options/CommonOptions';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';
import { RubricaAnaliseGraficosResponse } from '@/types/rubricas/analise/graficos/RubricaAnaliseGraficosResponse';

type Filtros = {
  optionsAno: { label: string; value: string }[];
  optionsMes: { label: string; value: string }[];
  optionsMotivoExcecao: { label: string; value: string; total: number }[];
};

type AgnesGaugeChartData = {
  dataQa: string;
  value: number;
  label: string;
};

type AgnesLineChartData = {
  dataQa: string;
  labels: string[];
  series: AgnesLineChartSeries[];
};

type Graficos = {
  indicadorTotalRubricas: AgnesStatCardProps;
  indicadorTotalInconsistenciasFGTS: AgnesStatCardProps;
  indicadorTotalInconsistenciasINSS: AgnesStatCardProps;
  indicadorTotalInconsistenciasIRRF: AgnesStatCardProps;
  indicadorTotalInconsistenciasCPRP: AgnesStatCardProps;
  indicadorTotalRubricasExcluidas: AgnesStatCardProps;
  indicadorTotalRubricasInativas: AgnesStatCardProps;
  indicadorTotalRubricasNaoUtilizadas: AgnesStatCardProps;
  indicadorTotalDivergenciaNatureza: AgnesStatCardProps;
  indicadorTotalDivergenciaTipo: AgnesStatCardProps;
  graficoAnaliseRisco: AgnesDonutChartProps;
  graficoPanoramaGeralAnalises: AgnesBarLineProps;
  graficoPanoramaConformidade: AgnesGaugeChartData;
  graficoTipoRubrica: AgnesBarLineProps;
  graficoTop5NaturezasUtilizadas: AgnesBarLineProps;
  graficoTotalGrauExposicaoRisco: AgnesBarLineProps;
  graficoEvolucaoGrauExposicaoRisco: AgnesLineChartData;
  graficoDistribuicaoGrauExposicaoRisco: AgnesStackedBarProps;
  indicadorTotalExcecoes: AgnesStatCardProps;
};

const lista = <T,>(campo: string, valor: T[] | null | undefined): T[] => {
  if (!Array.isArray(valor)) {
    throw new Error(`Campo ausente ou inválido na resposta: ${campo}`);
  }

  return valor;
};

function buildGraficosData(data: RubricaAnaliseGraficosResponse): Graficos {
  return {
    indicadorTotalRubricas: {
      color: 'primary',
      icon: <FileText className="h-4 w-4" />,
      title: 'Total Rubricas',
      dataQa: 'indicador-total-rubricas',
      value: brl(data.indicadorTotalRubricas.value),
    },
    indicadorTotalInconsistenciasFGTS: {
      color: 'error',
      icon: <TriangleAlert className="h-4 w-4" />,
      title: 'Inconsistências FGTS',
      dataQa: 'indicador-total-inconsistencias-fgts',
      value: (
        <span>
          {brl(data.indicadorTotalInconsistenciasFGTS.value)}
          <span className="text-base font-medium ml-2">
            {brl(data.indicadorTotalInconsistenciasFGTS.percentage)}%
          </span>
        </span>
      ),
    },
    indicadorTotalInconsistenciasINSS: {
      color: 'error',
      icon: <TriangleAlert className="h-4 w-4" />,
      title: 'Inconsistências INSS',
      dataQa: 'indicador-total-inconsistencias-inss',
      value: (
        <span>
          {brl(data.indicadorTotalInconsistenciasINSS.value)}
          <span className="text-base font-medium ml-2">
            {brl(data.indicadorTotalInconsistenciasINSS.percentage)}%
          </span>
        </span>
      ),
    },
    indicadorTotalInconsistenciasIRRF: {
      color: 'error',
      icon: <TriangleAlert className="h-4 w-4" />,
      title: 'Inconsistências IRRF',
      dataQa: 'indicador-total-inconsistencias-irrf',
      value: (
        <span>
          {brl(data.indicadorTotalInconsistenciasIRRF.value)}
          <span className="text-base font-medium ml-2">
            {brl(data.indicadorTotalInconsistenciasIRRF.percentage)}%
          </span>
        </span>
      ),
    },
    indicadorTotalInconsistenciasCPRP: {
      color: 'error',
      icon: <TriangleAlert className="h-4 w-4" />,
      title: 'Inconsistências CPRP',
      dataQa: 'indicador-total-inconsistencias-cprp',
      value: (
        <span>
          {brl(data.indicadorTotalInconsistenciasCPRP.value)}
          <span className="text-base font-medium ml-2">
            {brl(data.indicadorTotalInconsistenciasCPRP.percentage)}%
          </span>
        </span>
      ),
    },
    indicadorTotalRubricasExcluidas: {
      icon: <ExternalLink className="h-4 w-4 text-agnes-primary" />,
      title: 'Rubricas Excluídas',
      dataQa: 'indicador-total-rubricas-excluidas',
      subtitle: `${brl(data.indicadorTotalRubricasExcluidas.percentage)}% em relação ao total`,
      value: brl(data.indicadorTotalRubricasExcluidas.value),
    },
    indicadorTotalRubricasInativas: {
      icon: <ExternalLink className="h-4 w-4 text-agnes-primary" />,
      title: 'Rubricas Inativas',
      dataQa: 'indicador-total-rubricas-inativas',
      subtitle: `${brl(data.indicadorTotalRubricasInativas.percentage)}% em relação ao total`,
      value: brl(data.indicadorTotalRubricasInativas.value),
    },
    indicadorTotalRubricasNaoUtilizadas: {
      icon: <ExternalLink className="h-4 w-4 text-agnes-primary" />,
      title: 'Rubricas Não Utilizadas',
      dataQa: 'indicador-total-rubricas-nao-utilizadas',
      subtitle: `${brl(data.indicadorTotalRubricasNaoUtilizadas.percentage)}% em relação ao total`,
      value: brl(data.indicadorTotalRubricasNaoUtilizadas.value),
    },
    indicadorTotalDivergenciaNatureza: {
      icon: <ExternalLink className="h-4 w-4 text-agnes-primary" />,
      title: 'Divergência na Natureza',
      dataQa: 'indicador-total-divergencia-natureza',
      subtitle: `${brl(data.indicadorTotalDivergenciaNatureza.percentage)}% em relação ao total`,
      value: brl(data.indicadorTotalDivergenciaNatureza.value),
    },
    indicadorTotalDivergenciaTipo: {
      icon: <ExternalLink className="h-4 w-4 text-agnes-primary" />,
      title: 'Divergência no Tipo',
      dataQa: 'indicador-total-divergencia-tipo',
      subtitle: `${brl(data.indicadorTotalDivergenciaTipo.percentage)}% em relação ao total`,
      value: brl(data.indicadorTotalDivergenciaTipo.value),
    },
    graficoAnaliseRisco: {
      dataQa: 'grafico-analise-risco',
      data: lista('graficoAnaliseRisco', data.graficoAnaliseRisco).map((item) => ({
        label: GRAU_EXPOSICAO_RISCO_NOMES[item.label],
        value: item.value,
        percentage: item.percentage,
        color: GRAU_EXPOSICAO_RISCO_CORES[item.label],
      })),
      legendPosition: 'bottom',
      legendMaxWidth: '300px',
    },
    graficoPanoramaGeralAnalises: {
      dataQa: 'panorama-geral-analises',
      items: lista('graficoPanoramaGeralAnalises', data.graficoPanoramaGeralAnalises),
    },
    graficoPanoramaConformidade: {
      dataQa: 'grafico-panorama-conformidade',
      value: data.graficoPanoramaConformidade.percentage,
      label: `${brl(data.graficoPanoramaConformidade.value)} de ${brl(data.graficoPanoramaConformidade.total)} analisadas`,
    },
    graficoTipoRubrica: {
      dataQa: 'grafico-tipo-rubrica',
      items: lista('graficoTipoRubrica', data.graficoTipoRubrica),
    },
    graficoTop5NaturezasUtilizadas: {
      dataQa: 'grafico-top-5-naturezas-utilizadas',
      items: lista('graficoTop5NaturezasUtilizadas', data.graficoTop5NaturezasUtilizadas),
    },
    graficoTotalGrauExposicaoRisco: {
      dataQa: 'grafico-total-grau-exposicao-risco',
      items: lista('graficoTotalGrauExposicaoRisco', data.graficoTotalGrauExposicaoRisco).map(
        (item) => ({
          label: GRAU_EXPOSICAO_RISCO_NOMES[item.label],
          value: item.value,
          percentage: item.percentage,
          color: GRAU_EXPOSICAO_RISCO_CORES[item.label],
        }),
      ),
    },
    graficoEvolucaoGrauExposicaoRisco: {
      dataQa: 'grafico-evolucao-grau-exposicao-risco',
      labels: data.graficoEvolucaoGrauExposicaoRisco.labels,
      series: lista(
        'graficoEvolucaoGrauExposicaoRisco.series',
        data.graficoEvolucaoGrauExposicaoRisco.series,
      ).map((serie) => ({
        label: GRAU_EXPOSICAO_RISCO_NOMES[serie.label],
        data: serie.data,
        color: GRAU_EXPOSICAO_RISCO_CORES[serie.label],
      })),
    },
    indicadorTotalExcecoes: {
      color: 'warning',
      icon: <TriangleAlert className="h-5 w-5" />,
      title: 'Total de exceções',
      dataQa: 'indicador-total-rubricas-excecoes',
      subtitle: 'rubricas com incidência suspensa (S-1070)',
      value: brl(data.indicadorTotalExcecoes.total),
      appendInner: (
        <AgnesAlert preset={'warning'}>
          <AgnesAlertTitle>Exposição em alto risco</AgnesAlertTitle>

          <AgnesAlertContent>
            <b>{brl(data.indicadorTotalExcecoes.percentage)}%</b>
            <span className="mx-1">·</span>
            {brl(data.indicadorTotalExcecoes.value)} exceções
            <span className="mx-1">·</span>
            Crítico + Elevado entre as exceções selecionadas
          </AgnesAlertContent>
        </AgnesAlert>
      ),
    },
    graficoDistribuicaoGrauExposicaoRisco: {
      dataQa: 'grafico-distribuicao-grau-exposicao-risco',
      data: lista(
        'graficoDistribuicaoGrauExposicaoRisco',
        data.graficoDistribuicaoGrauExposicaoRisco,
      ).map((item) => ({
        label: GRAU_EXPOSICAO_RISCO_NOMES[item.label],
        value: item.value,
        percentage: item.percentage,
        color: GRAU_EXPOSICAO_RISCO_CORES[item.label],
      })),
      label: 'Distribuição por grau de risco',
      barSize: 16,
    },
  };
}

export function TabGraficos() {
  const [isRequestingFiltros, setIsRequestingFiltros] = React.useState(false);
  const [isRequestingGraficos, setIsRequestingGraficos] = React.useState(false);

  const [filtrosOptions, setFiltrosOptions] = React.useState<Filtros>();
  const [filtroAnoValue, setFiltroAnoValue] = React.useState(SELECT_ALL_VALUES_OPTION.value);
  const [filtroMesValue, setFiltroMesValue] = React.useState(SELECT_ALL_VALUES_OPTION.value);
  const [filtroMotivoExcecaoValue] = React.useState(undefined);

  const [graficosData, setGraficosData] = React.useState<Graficos>();
  const [error, setError] = React.useState(false);

  const [cardGrauExposicaoRiscoTab, setCardGrauExposicaoRiscoTab] = React.useState('matriz');

  React.useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    setIsRequestingFiltros(true);
    setFiltrosOptions({
      optionsAno: [SELECT_ALL_VALUES_OPTION],
      optionsMes: [SELECT_ALL_VALUES_OPTION],
      optionsMotivoExcecao: [],
    });

    ServiceRubricas.consultarFiltros(controller.signal)
      .then(({ data }) => {
        if (!ignore) {
          setFiltrosOptions({
            optionsAno: [SELECT_ALL_VALUES_OPTION, ...toOptions(data.optionsAno)],
            optionsMes: [
              SELECT_ALL_VALUES_OPTION,
              ...toOptions(data.optionsMes, { labelTransformerFn: (v) => toLocaleMonthName(v) }),
            ],
            optionsMotivoExcecao: [...data.optionsMotivoExcecao],
          });
        }
      })
      .catch((err) => {
        if (!ignore)
          notify.error(mensagemErro(err, 'Houve um problema ao buscar as opções para os filtros.'));
      })
      .finally(() => {
        if (!ignore) setIsRequestingFiltros(false);
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  React.useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    setGraficosData(undefined);
    setError(false);
    setIsRequestingGraficos(true);

    ServiceRubricas.consultarGraficos({
      ano: filtroAnoValue,
      mes: filtroMesValue,
      motivoExcecao: filtroMotivoExcecaoValue,
    })
      .then(({ data }) => {
        if (ignore) return;

        try {
          setGraficosData(buildGraficosData(data));
        } catch (err) {
          setError(true);
          console.error(err);
          notify.error(`Resposta dos gráficos em formato inesperado: ${(err as Error).message}`);
        }
      })
      .catch((err) => {
        if (!ignore) {
          setError(true);
          notify.error(mensagemErro(err, 'Houve um problema ao buscar os dados dos gráficos.'));
        }
      })
      .finally(() => {
        if (!ignore) setIsRequestingGraficos(false);
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [filtroAnoValue, filtroMesValue, filtroMotivoExcecaoValue]);

  return (
    <div className="space-y-4">
      <div className="p-4 flex flex-wrap items-center gap-3 bg-agnes-bg-surface rounded-xl border border-agnes-border">
        <span className="text-sm font-medium text-agnes-primary">Período</span>

        <span className="text-sm text-agnes-text-muted">Ano</span>
        <AgnesSelect
          value={filtroAnoValue}
          onValueChange={setFiltroAnoValue}
          disabled={
            isRequestingFiltros || !filtrosOptions || filtrosOptions.optionsAno.length === 1
          }
        >
          <AgnesSelectTrigger className="w-40">
            <AgnesSelectValue />
          </AgnesSelectTrigger>

          <AgnesSelectContent>
            {filtrosOptions?.optionsAno.map((option) => (
              <AgnesSelectItem key={option.value} value={option.value}>
                {option.label}
              </AgnesSelectItem>
            ))}
          </AgnesSelectContent>
        </AgnesSelect>

        <span className="text-sm text-agnes-text-muted">Mês</span>
        <AgnesSelect
          value={filtroMesValue}
          onValueChange={setFiltroMesValue}
          disabled={
            isRequestingFiltros || !filtrosOptions || filtrosOptions.optionsMes.length === 1
          }
        >
          <AgnesSelectTrigger className="w-40">
            <AgnesSelectValue />
          </AgnesSelectTrigger>

          <AgnesSelectContent>
            {filtrosOptions?.optionsMes.map((option) => (
              <AgnesSelectItem key={option.value} value={option.value}>
                {option.label}
              </AgnesSelectItem>
            ))}
          </AgnesSelectContent>
        </AgnesSelect>

        {filtroAnoValue === 'TODOS' && filtroMesValue === 'TODOS' && (
          <AgnesBadge variant="success" size="lg" className="ml-auto">
            Visão consolidada
          </AgnesBadge>
        )}
      </div>

      {error ? (
        <AgnesCard className="rounded-xl" dataQa="graficos-erro-carregamento">
          <AgnesCardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <TriangleAlert className="h-6 w-6 text-agnes-error" />
            <AgnesCardTitle>Não foi possível carregar os gráficos</AgnesCardTitle>
            <p className="text-sm text-agnes-text-muted">
              Tente novamente em instantes ou ajuste os filtros.
            </p>
          </AgnesCardContent>
        </AgnesCard>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {!isRequestingGraficos && !!graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalRubricas}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalInconsistenciasFGTS}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalInconsistenciasINSS}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalInconsistenciasIRRF}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalInconsistenciasCPRP}
              />
            ) : (
              <AgnesSkeletonCard />
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalRubricasExcluidas}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalRubricasInativas}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalRubricasNaoUtilizadas}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalDivergenciaNatureza}
              />
            ) : (
              <AgnesSkeletonCard />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard
                discreet
                className="rounded-xl"
                {...graficosData.indicadorTotalDivergenciaTipo}
              />
            ) : (
              <AgnesSkeletonCard />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl">
                <AgnesCardHeader>
                  <AgnesCardTitle>Análise de risco</AgnesCardTitle>
                  <Info className="w-5 h-5 text-agnes-text-muted" />
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-center justify-center">
                  {graficosData.graficoAnaliseRisco.data.every((item) => !item.value) ? (
                    <div className="h-80 flex items-center justify-center text-agnes-text-muted">
                      <p>Não há dados disponíveis</p>
                    </div>
                  ) : (
                    <AgnesDonutChart {...graficosData.graficoAnaliseRisco} />
                  )}
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[600px]" />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl">
                <AgnesCardHeader>
                  <AgnesCardTitle>Panorama geral das análises</AgnesCardTitle>
                  <Info className="w-5 h-5 text-agnes-text-muted" />
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-stretch justify-center">
                  {graficosData.graficoPanoramaGeralAnalises.items.every((item) => !item.value) ? (
                    <div className="h-80 flex items-center justify-center text-agnes-text-muted">
                      <p>Não há dados disponíveis</p>
                    </div>
                  ) : (
                    <AgnesBarLine {...graficosData.graficoPanoramaGeralAnalises} />
                  )}
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[600px]" />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl">
                <AgnesCardHeader>
                  <AgnesCardTitle>Panorama de conformidade</AgnesCardTitle>
                  <Info className="w-5 h-5 text-agnes-text-muted" />
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-center justify-center">
                  <AgnesGaugeChart {...graficosData.graficoPanoramaConformidade} showValue />
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[300px]" />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl">
                <AgnesCardHeader>
                  <AgnesCardTitle>Tipo de Rubrica</AgnesCardTitle>
                  <Info className="w-5 h-5 text-agnes-text-muted" />
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-stretch justify-center">
                  <AgnesBarLine {...graficosData.graficoTipoRubrica} />
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[300px]" />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl">
                <AgnesCardHeader>
                  <AgnesCardTitle>Top 5 naturezas de rubricas usadas na folha</AgnesCardTitle>
                  <Info className="w-5 h-5 text-agnes-text-muted" />
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-stretch justify-center">
                  <AgnesBarLine {...graficosData.graficoTop5NaturezasUtilizadas} />
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[300px]" />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            <AgnesCard className="rounded-xl">
              <AgnesCardHeader>
                <AgnesCardTitle>
                  Gráficos por exposição ao risco e matriz de exposição
                </AgnesCardTitle>
                <Info className="w-5 h-5 text-agnes-text-muted" />
              </AgnesCardHeader>

              <AgnesCardContent className="h-full flex flex-col items-stretch justify-center">
                <AgnesTabs
                  value={cardGrauExposicaoRiscoTab}
                  onValueChange={setCardGrauExposicaoRiscoTab}
                >
                  <AgnesTabsList>
                    <AgnesTabsTrigger value="matriz">Matriz de exposição</AgnesTabsTrigger>
                    <AgnesTabsTrigger value="exposicao">Exposição ao risco</AgnesTabsTrigger>
                  </AgnesTabsList>

                  <AgnesTabsContent value="matriz">
                    <MatrizGrauExposicaoRisco />
                  </AgnesTabsContent>

                  <AgnesTabsContent value="exposicao">
                    {!isRequestingGraficos && graficosData ? (
                      <AgnesBarLine {...graficosData.graficoTotalGrauExposicaoRisco} />
                    ) : (
                      <AgnesSkeletonCard className="h-[300px]" />
                    )}
                  </AgnesTabsContent>
                </AgnesTabs>
              </AgnesCardContent>
            </AgnesCard>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl">
                <AgnesCardHeader>
                  <AgnesCardTitle>Evolução do grau de risco - linha do tempo</AgnesCardTitle>
                  <Info className="w-5 h-5 text-agnes-text-muted" />
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-center justify-center">
                  {graficosData.graficoEvolucaoGrauExposicaoRisco.series.every(
                    (item) => !item.data.length,
                  ) ? (
                    <div className="h-80 flex items-center justify-center text-agnes-text-muted">
                      <p>Não há dados disponíveis</p>
                    </div>
                  ) : (
                    <AgnesLineChart {...graficosData.graficoEvolucaoGrauExposicaoRisco} />
                  )}
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[300px]" />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
            {!isRequestingGraficos && graficosData ? (
              <AgnesStatCard className="rounded-xl" {...graficosData.indicadorTotalExcecoes} />
            ) : (
              <AgnesSkeletonCard className="h-[250px]" />
            )}

            {!isRequestingGraficos && graficosData ? (
              <AgnesCard className="rounded-xl lg:col-span-2">
                <AgnesCardHeader>
                  <AgnesCardTitle>
                    <Info className="w-5 h-5 text-agnes-text-muted" />
                    Exceções por motivo
                  </AgnesCardTitle>
                </AgnesCardHeader>

                <AgnesCardContent className="h-full flex flex-col items-start justify-around">
                  {!isRequestingFiltros && filtrosOptions && (
                    <div className="flex flex-row items-start justify-start gap-2 flex-wrap">
                      {filtrosOptions?.optionsMotivoExcecao.map((option) => (
                        <AgnesBadge
                          variant="secondary"
                          size="lg"
                          key={option.value}
                          dataQa={`opcoes-grau-exposicao-risco-${option.value}`}
                        >
                          {option.label}
                          <span className="mx-1">·</span>
                          {option.total}
                        </AgnesBadge>
                      ))}
                    </div>
                  )}

                  <AgnesStackedBar {...graficosData.graficoDistribuicaoGrauExposicaoRisco} />
                </AgnesCardContent>
              </AgnesCard>
            ) : (
              <AgnesSkeletonCard className="h-[250px] rounded-xl lg:col-span-2" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
